import * as THREE from 'three';
import {isDashDotLIneNodeMaterial} from './guards/isDashDotLIneNodeMaterial';
import {ScreenSpaceDashedLineNodeMaterial} from './ScreenSpaceDashedLineNodeMaterial';
import {ThinChainLIneNodeMaterial} from './ThinChainLineNodeMaterial';

import {SketchLineMaterialDepthPolicy} from './SketchLineMaterialDepthPolicy';


/**
 * 线材质状态枚举。
 */
export enum LineMaterialState {
    /** 默认状态材质。 */
    Default = 'default',
    /** 选中状态材质。 */
    Selected = 'selected',
    /** 移入状态材质。 */
    Hover = 'hover',
}


/**
 * 线基础样式枚举。
 * 说明：
 * 1. 该状态描述对象默认应呈现的线型；
 * 2. 交互状态（移入、选中）必须建立在该基础样式之上；
 * 3. 后续若扩展普通虚线、投影线等，只扩展该枚举即可。
 */
export enum SketchEdgeBaseStyle {
    /** 普通实线。 */
    Basic = 'basic',
    /** 构造线虚线。 */
    Construction = 'construction',
}


/**
 * 线材质创建参数。
 */
export interface LineMaterialBuildOptions {
    /** 材质状态。 */
    state: LineMaterialState;
    /** 基础线型状态。 */
    baseStyle?: SketchEdgeBaseStyle;
    /** 源线材质（仅用于识别实线/虚线类型）。 */
    sourceMaterial?: unknown;
    /** 目标颜色（兼容保留，当前固定材质实现不使用）。 */
    color: THREE.ColorRepresentation;
    /** 回退线宽（兼容保留，当前固定材质实现不使用）。 */
    lineWidth?: number;
    isFixed?: boolean;
}

/**
 * 屏幕空间虚线材质创建参数。
 */
export interface ScreenSpaceDashedLineMaterialBuildOptions {
    /** 折线世界坐标点列。 */
    points: THREE.Vector3[];
    /** 材质颜色。 */
    color: THREE.ColorRepresentation;
    /** 线宽。 */
    linewidth: number;
    /** 屏幕空间虚线段长度。 */
    dashSize: number;
    /** 屏幕空间虚线间隔长度。 */
    gapSize: number;
    /** 材质透明度。 */
    opacity: number;
    /** 是否透明。 */
    transparent: boolean;
    /** 材质名称。 */
    name: string;
}

/**
 * 草图线材质联合类型。
 * 说明：
 * 1. 普通线、预览线等仍沿用 classic line material；
 * 2. 正式构造线与中心线会切到 `ThinChainLIneNodeMaterial`；
 * 3. 该联合类型用于收口构造线迁移期间的返回值与缓存边界。
 */
export type SketchLineMaterial = THREE.LineBasicMaterial | THREE.LineDashedMaterial | ThinChainLIneNodeMaterial;

/**
 * 线材质名称枚举。
 */
enum LineMaterialName {
    /** 实线-默认状态。 */
    BasicDefault = '草图线-实线-默认材质',
    /** 实线-非活动默认状态。 */
    BasicInactive = '草图线-实线-非活动默认材质',
    /** 实线-选中状态。 */
    BasicSelected = '草图线-实线-选中材质',
    /** 实线-移入状态。 */
    BasicHover = '草图线-实线-移入材质',
    /** 虚线-默认状态。 */
    DashedDefault = '草图线-虚线-默认材质',
    /** 虚线-非活动默认状态。 */
    DashedInactive = '草图线-虚线-非活动默认材质',
    /** 虚线-选中状态。 */
    DashedSelected = '草图线-虚线-选中材质',
    /** 虚线-移入状态。 */
    DashedHover = '草图线-虚线-移入材质',
    /** 构造线-默认状态。 */
    ConstructionDefault = '草图线-构造线-默认材质',
    /** 构造线-非活动默认状态。 */
    ConstructionInactive = '草图线-构造线-非活动默认材质',
    /** 构造线-选中状态。 */
    ConstructionSelected = '草图线-构造线-选中材质',
    /** 构造线-移入状态。 */
    ConstructionHover = '草图线-构造线-移入材质',
    /** 固定线-默认状态。 */
    FixedDefault = '草图线-固定-默认材质',
    /** 固定线-非活动默认状态。 */
    FixedInactive = '草图线-固定-非活动默认材质',
    /** 特征线-默认状态。 */
    FeatureDefault = '特征线-默认材质',
    /** 特征线-选中状态。 */
    FeatureSelected = '特征线-选中材质',
    /** 特征线-移入状态。 */
    FeatureHover = '特征线-移入材质',
    /** 特征面移入边界线。 */
    FeatureFaceHoverBoundary = '特征线-面移入边界材质',
    /** 点捕捉-相切预览辅助线。 */
    PointSnapTangentPreviewGuide = '点捕捉-相切预览-屏幕空间虚线材质',
    /** 点捕捉-相切高亮辅助线。 */
    PointSnapTangentHighlightGuide = '点捕捉-相切高亮-屏幕空间虚线材质',
    /** 点捕捉-与点水平竖直追踪辅助线。 */
    PointSnapPointHvGuide = '点捕捉-与点水平竖直-屏幕空间虚线材质',
    /** 实体引用预览-实线状态。 */
    ReferencePreviewBasic = '草图线-实体引用预览-实线材质',
    /** 实体引用预览-构造线状态。 */
    ReferencePreviewConstruction = '草图线-实体引用预览-构造线材质',
    /** 实体引用来源高亮-实线状态。 */
    ReferenceSourceHighlightBasic = '草图线-实体引用来源高亮-实线材质',
    /** 实体引用来源高亮-构造线状态。 */
    ReferenceSourceHighlightConstruction = '草图线-实体引用来源高亮-构造线材质',
}

/** 实线与虚线统一线宽。 */
const LINE_WIDTH = 3;
/** 虚线段长。 */
const DASH_SIZE = 3;
/** 虚线间隔。 */
const GAP_SIZE = 2;

/** 预览颜色。 */
const PREVIEW_COLOR = 0x53a9ff;


/** 点捕捉辅助线深橙色，保证白色背景下仍然清晰可见。 */
export const POINT_SNAP_GUIDE_DARK_ORANGE_COLOR = 0xFF8C00;


/** 与点水平/竖直追踪线虚线段屏幕像素长度。 */
const POINT_HV_SCREEN_SPACE_DASH_SIZE_PX = 7;
/** 与点水平/竖直追踪线虚线间隔屏幕像素长度。 */
const POINT_HV_SCREEN_SPACE_GAP_SIZE_PX = 4;
/** 与点水平/竖直追踪线颜色。 */
const POINT_HV_TRACKING_LINE_COLOR = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;


/** 相切候选辅助线颜色。 */
const TANGENT_GUIDE_PREVIEW_COLOR = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;
/** 相切、平行、垂直命中高亮辅助线颜色。 */
const POINT_SNAP_GUIDE_HIGHLIGHT_COLOR = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;
/** 点捕捉辅助线默认线宽。 */
const POINT_SNAP_GUIDE_LINE_WIDTH = 2;
/** 点捕捉辅助线默认透明度。 */
const POINT_SNAP_GUIDE_OPACITY = 0.85;
/** 点捕捉辅助线虚线段屏幕像素长度。 */
const POINT_SNAP_GUIDE_DASH_SIZE_PX = 4;
/** 点捕捉辅助线虚线间隔屏幕像素长度。 */
const POINT_SNAP_GUIDE_GAP_SIZE_PX = 3;

/** 实体引用来源高亮颜色。 */
const REFERENCE_SOURCE_HIGHLIGHT_COLOR = 0xe0b400;
/** 悬空转换实体引用的默认警告色。 */
const REFERENCE_DANGLING_COLOR = 0x9a6700;
/** 转换实体引用计算失败的默认错误色。 */
const REFERENCE_FAILED_COLOR = 0xd93025;
/** 默认状态颜色。 */
const LINE_COLOR_DEFAULT = '#8b8b8b';
/** 非活动默认态颜色。 */
const LINE_COLOR_INACTIVE = 0x90979f;
/** 非活动默认态透明度。 */
const LINE_INACTIVE_OPACITY = 0.8;
/** 选中状态颜色。 */
const LINE_COLOR_SELECTED = '#0043ff';
/** 移入状态颜色。 */
const LINE_COLOR_HOVER = '#f48611';
/** 固定线默认颜色。 */
const LINE_COLOR_FIXED = '#000000';
/** 特征线默认颜色。 */
const FEATURE_LINE_COLOR_DEFAULT = '#000000';
/** 特征线选中颜色。 */
const FEATURE_LINE_COLOR_SELECTED = '#0043ff';
/** 特征线移入颜色。 */
const FEATURE_LINE_COLOR_HOVER = '#f48611';

/** 特征线默认浏览态初始透明度：0 表示纯着色、不绘制可见边线，交互态仍通过 Hover/Selected 显色。 */
const FEATURE_LINE_DEFAULT_OPACITY = 0;


/**
 * 线材质工厂。
 * 说明：
 * 1. 与 PointsMaterialFactory 一致，按“基础线型状态 + 交互状态”返回固定材质；
 * 2. 每个状态参数写死，避免根据源材质动态克隆导致表现漂移；
 * 3. `sourceMaterial` 仅保留兼容推断用途，当前推荐直接传入 `baseStyle`。
 */
export class LineMaterialFactory {
    /** 实线-默认状态缓存。 */
    public static basicDefaultMaterial: THREE.LineBasicMaterial | null = null;
    /** 实线-非活动默认状态缓存。 */
    public static basicInactiveMaterial: THREE.LineBasicMaterial | null = null;
    /** 实线-选中状态缓存。 */
    public static basicSelectedMaterial: THREE.LineBasicMaterial | null = null;
    /** 实线-移入状态缓存。 */
    public static basicHoverMaterial: THREE.LineBasicMaterial | null = null;

    /** 虚线-默认状态缓存。 */
    public static dashedDefaultMaterial: THREE.LineDashedMaterial | null = null;
    /** 虚线-非活动默认状态缓存。 */
    public static dashedInactiveMaterial: THREE.LineDashedMaterial | null = null;
    /** 虚线-选中状态缓存。 */
    public static dashedSelectedMaterial: THREE.LineDashedMaterial | null = null;
    /** 虚线-移入状态缓存。 */
    public static dashedHoverMaterial: THREE.LineDashedMaterial | null = null;

    /** 构造线-默认状态缓存。 */
    public static constructionDefaultMaterial: THREE.LineDashedMaterial | null = null;
    /** 构造线-非活动默认状态缓存。 */
    public static constructionInactiveMaterial: THREE.LineDashedMaterial | null = null;
    /** 构造线-选中状态缓存。 */
    public static constructionSelectedMaterial: THREE.LineDashedMaterial | null = null;
    /** 构造线-移入状态缓存。 */
    public static constructionHoverMaterial: THREE.LineDashedMaterial | null = null;
    /** 固定线-默认状态缓存。 */
    public static fixedDefaultMaterial: THREE.LineBasicMaterial | null = null;
    /** 固定线-非活动默认状态缓存。 */
    public static fixedInactiveMaterial: THREE.LineBasicMaterial | null = null;
    /** 特征线-默认状态缓存。 */
    public static featureDefaultMaterial: THREE.LineBasicMaterial | null = null;
    /** 特征线-选中状态缓存。 */
    public static featureSelectedMaterial: THREE.LineBasicMaterial | null = null;
    /** 特征线-移入状态缓存。 */
    public static featureHoverMaterial: THREE.LineBasicMaterial | null = null;
    /** 特征面移入边界线材质缓存。 */
    public static featureFaceHoverBoundaryMaterial: THREE.LineBasicMaterial | null = null;

    
    /** 当前特征线默认态透明度。 */
    private static featureLineDefaultOpacity: number = FEATURE_LINE_DEFAULT_OPACITY;
    

    /** 预览-实线材质缓存。 */
    public static previewBasicMaterial: THREE.LineBasicMaterial | null = null;
    /** 预览-虚线材质缓存（构造线预览）。 */
    public static previewDashedMaterial: THREE.LineDashedMaterial | null = null;
    /** 实体引用预览-实线材质缓存。 */
    public static referencePreviewBasicMaterial: THREE.LineBasicMaterial | null = null;
    /** 实体引用预览-构造线材质缓存。 */
    public static referencePreviewConstructionMaterial: THREE.LineDashedMaterial | null = null;
    /** 实体引用来源高亮-实线材质缓存。 */
    public static referenceSourceHighlightBasicMaterial: THREE.LineBasicMaterial | null = null;
    /** 实体引用来源高亮-构造线材质缓存。 */
    public static referenceSourceHighlightConstructionMaterial: THREE.LineDashedMaterial | null = null;
    /** 悬空引用-默认状态虚线材质缓存。 */
    public static referenceDanglingDefaultMaterial: THREE.LineDashedMaterial | null = null;
    /** 悬空引用-选中状态虚线材质缓存。 */
    public static referenceDanglingSelectedMaterial: THREE.LineDashedMaterial | null = null;
    /** 悬空引用-移入状态虚线材质缓存。 */
    public static referenceDanglingHoverMaterial: THREE.LineDashedMaterial | null = null;
    /** 失败引用-默认状态材质缓存。 */
    public static referenceFailedDefaultMaterial: THREE.LineBasicMaterial | null = null;
    /** 失败引用-选中状态材质缓存。 */
    public static referenceFailedSelectedMaterial: THREE.LineBasicMaterial | null = null;
    /** 失败引用-移入状态材质缓存。 */
    public static referenceFailedHoverMaterial: THREE.LineBasicMaterial | null = null;
    /** 切线预览-虚线材质缓存（深橙色虚线）。 */
    public static tangentPreviewMaterial: THREE.LineDashedMaterial | null = null;
    /** 切线高亮-虚线材质缓存（深橙色虚线）。 */
    public static tangentHighlightMaterial: THREE.LineDashedMaterial | null = null;
    /** 垂线预览-虚线材质缓存（深橙色虚线）。 */
    public static perpendicularPreviewMaterial: THREE.LineDashedMaterial | null = null;
    /** 垂线高亮-虚线材质缓存（深橙色虚线）。 */
    public static perpendicularHighlightMaterial: THREE.LineDashedMaterial | null = null;
    /** 平行预览-虚线材质缓存（深橙色虚线）。 */
    public static parallelPreviewMaterial: THREE.LineDashedMaterial | null = null;
    /** 平行高亮-虚线材质缓存（深橙色虚线）。 */
    public static parallelHighlightMaterial: THREE.LineDashedMaterial | null = null;
    /** 与点水平/竖直预览-虚线材质缓存（深橙色虚线）。 */
    public static pointHvPreviewMaterial: THREE.LineDashedMaterial | null = null;

    /**
     * 获取线材质。
     * @param options 创建参数。
     * @returns 线材质实例。
     */
    public static getLineMaterial(options: LineMaterialBuildOptions): SketchLineMaterial {
        // 保留兼容参数，明确当前固定状态材质实现不使用它们。
        void options.color;
        void options.lineWidth;

        const resolvedBaseStyle = options.baseStyle
            ?? this.resolveBaseStyleBySourceMaterial(options.sourceMaterial);
        return this.getLineMaterialByBaseStyle(resolvedBaseStyle, options.state, options.isFixed);
    }

    /**
     * 清空线材质缓存。
     */
    public static clearCache(): void {
        /*
         * 工厂缓存拥有这些共享材质的生命周期。先按对象身份去重释放，再清空引用，
         * 避免主题切换、测试隔离或 Viewer 销毁后遗留 GPU program 与材质资源。
         */
        new Set([
            this.basicDefaultMaterial,
            this.basicInactiveMaterial,
            this.basicSelectedMaterial,
            this.basicHoverMaterial,
            this.dashedDefaultMaterial,
            this.dashedInactiveMaterial,
            this.dashedSelectedMaterial,
            this.dashedHoverMaterial,
            this.constructionDefaultMaterial,
            this.constructionInactiveMaterial,
            this.constructionSelectedMaterial,
            this.constructionHoverMaterial,
            this.fixedDefaultMaterial,
            this.fixedInactiveMaterial,
            this.featureDefaultMaterial,
            this.featureSelectedMaterial,
            this.featureHoverMaterial,
            this.featureFaceHoverBoundaryMaterial,
            this.previewBasicMaterial,
            this.previewDashedMaterial,
            this.referencePreviewBasicMaterial,
            this.referencePreviewConstructionMaterial,
            this.referenceSourceHighlightBasicMaterial,
            this.referenceSourceHighlightConstructionMaterial,
            this.referenceDanglingDefaultMaterial,
            this.referenceDanglingSelectedMaterial,
            this.referenceDanglingHoverMaterial,
            this.referenceFailedDefaultMaterial,
            this.referenceFailedSelectedMaterial,
            this.referenceFailedHoverMaterial,
            this.tangentPreviewMaterial,
            this.tangentHighlightMaterial,
            this.perpendicularPreviewMaterial,
            this.perpendicularHighlightMaterial,
            this.parallelPreviewMaterial,
            this.parallelHighlightMaterial,
            this.pointHvPreviewMaterial,
        ]).forEach((material) => material?.dispose());

        this.basicDefaultMaterial = null;
        this.basicInactiveMaterial = null;
        this.basicSelectedMaterial = null;
        this.basicHoverMaterial = null;
        this.dashedDefaultMaterial = null;
        this.dashedInactiveMaterial = null;
        this.dashedSelectedMaterial = null;
        this.dashedHoverMaterial = null;
        this.constructionDefaultMaterial = null;
        this.constructionInactiveMaterial = null;
        this.constructionSelectedMaterial = null;
        this.constructionHoverMaterial = null;
        this.fixedDefaultMaterial = null;
        this.fixedInactiveMaterial = null;
        this.featureDefaultMaterial = null;
        this.featureSelectedMaterial = null;
        this.featureHoverMaterial = null;
        this.featureFaceHoverBoundaryMaterial = null;
        this.previewBasicMaterial = null;
        this.previewDashedMaterial = null;
        this.referencePreviewBasicMaterial = null;
        this.referencePreviewConstructionMaterial = null;
        this.referenceSourceHighlightBasicMaterial = null;
        this.referenceSourceHighlightConstructionMaterial = null;
        this.referenceDanglingDefaultMaterial = null;
        this.referenceDanglingSelectedMaterial = null;
        this.referenceDanglingHoverMaterial = null;
        this.referenceFailedDefaultMaterial = null;
        this.referenceFailedSelectedMaterial = null;
        this.referenceFailedHoverMaterial = null;
        this.tangentPreviewMaterial = null;
        this.tangentHighlightMaterial = null;
        this.perpendicularPreviewMaterial = null;
        this.perpendicularHighlightMaterial = null;
        this.parallelPreviewMaterial = null;
        this.parallelHighlightMaterial = null;
        this.pointHvPreviewMaterial = null;
    }

    
    /**
     * 获取切线预览虚线材质（深橙色虚线，单例）。
     * 说明：用于画线时悬停圆/圆弧/椭圆边缘时显示的候选切线预览。
     * @returns 切线预览虚线材质实例。
     */
    public static getTangentPreviewMaterial(): THREE.LineDashedMaterial {
        if (!this.tangentPreviewMaterial) {
            this.tangentPreviewMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.tangentPreviewMaterial.name = '切线预览-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.tangentPreviewMaterial);
    }

    /**
     * 获取切线高亮虚线材质（深橙色虚线，单例）。
     * 说明：预览线方向与切线方向重合时，将该切线替换为此材质，视觉上与预览线融合。
     * @returns 切线高亮虚线材质实例。
     */
    public static getTangentHighlightMaterial(): THREE.LineDashedMaterial {
        if (!this.tangentHighlightMaterial) {
            this.tangentHighlightMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.tangentHighlightMaterial.name = '切线高亮-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.tangentHighlightMaterial);
    }

    /**
     * 获取垂线预览虚线材质（深橙色虚线，单例）。
     * 说明：用于画线时悬停线段/圆/圆弧/椭圆边缘时显示的候选垂线预览。
     * @returns 垂线预览虚线材质实例。
     */
    public static getPerpendicularPreviewMaterial(): THREE.LineDashedMaterial {
        if (!this.perpendicularPreviewMaterial) {
            this.perpendicularPreviewMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.perpendicularPreviewMaterial.name = '垂线预览-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.perpendicularPreviewMaterial);
    }

    /**
     * 获取垂线高亮虚线材质（深橙色虚线，单例）。
     * 说明：预览线方向与垂线方向重合时，将该垂线替换为此材质，视觉上与预览线融合。
     * @returns 垂线高亮虚线材质实例。
     */
    public static getPerpendicularHighlightMaterial(): THREE.LineDashedMaterial {
        if (!this.perpendicularHighlightMaterial) {
            this.perpendicularHighlightMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.perpendicularHighlightMaterial.name = '垂线高亮-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.perpendicularHighlightMaterial);
    }

    /**
     * 获取平行预览虚线材质（深橙色虚线，单例）。
     * 说明：用于画线时悬停已有直线边缘时显示的候选平行线预览。
     * @returns 平行预览虚线材质实例。
     */
    public static getParallelPreviewMaterial(): THREE.LineDashedMaterial {
        if (!this.parallelPreviewMaterial) {
            this.parallelPreviewMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.parallelPreviewMaterial.name = '平行预览-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.parallelPreviewMaterial);
    }

    /**
     * 获取平行高亮虚线材质（深橙色虚线，单例）。
     * 说明：预览线方向与平行线方向重合时，将该平行线替换为此材质，视觉上与预览线融合。
     * @returns 平行高亮虚线材质实例。
     */
    public static getParallelHighlightMaterial(): THREE.LineDashedMaterial {
        if (!this.parallelHighlightMaterial) {
            this.parallelHighlightMaterial = new THREE.LineDashedMaterial({
                color: POINT_SNAP_GUIDE_DARK_ORANGE_COLOR,
                linewidth: 2,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.85,
                transparent: true,
            });
            this.parallelHighlightMaterial.name = '平行高亮-深橙色虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.parallelHighlightMaterial);
    }

    /**
     * 获取与点水平/竖直捕捉预览虚线材质（深橙色虚线，单例）。
     * 说明：用于鼠标与草图点水平或竖直对齐时显示的追踪参考线。
     * @returns 与点水平/竖直预览虚线材质实例。
     */
    public static getPointHvPreviewMaterial(): THREE.LineDashedMaterial {
        if (!this.pointHvPreviewMaterial) {
            this.pointHvPreviewMaterial = new THREE.LineDashedMaterial({
                color: POINT_HV_TRACKING_LINE_COLOR,
                linewidth: 1,
                dashSize: 4,
                gapSize: 3,
                opacity: 0.8,
                transparent: true,
            });
            this.pointHvPreviewMaterial.name = '与点水平竖直-预览虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.pointHvPreviewMaterial);
    }

    
    /**
     * 创建屏幕空间虚线材质。
     *
     * 设计说明：
     * 1. 点捕捉辅助线属于运行时预览对象，不能复用正式草图线材质；
     * 2. `ScreenSpaceDashedLineNodeMaterial` 以屏幕像素表达虚线节奏，缩放视图时视觉密度稳定；
     * 3. 该材质持有折线点列 uniform，每条辅助线必须独享实例。
     *
     * @param options 屏幕空间虚线材质参数。
     * @returns 已按参数初始化的屏幕空间虚线材质。
     */
    public static createScreenSpaceDashedLineMaterial(
        options: ScreenSpaceDashedLineMaterialBuildOptions,
    ): ScreenSpaceDashedLineNodeMaterial {
        const material = new ScreenSpaceDashedLineNodeMaterial({
            color: options.color,
            linewidth: options.linewidth,
            dashSize: options.dashSize,
            gapSize: options.gapSize,
            opacity: options.opacity,
            transparent: options.transparent,
            points: options.points,
        });
        material.name = options.name;
        return SketchLineMaterialDepthPolicy.apply(material);
    }

    /**
     * 创建相切预览辅助线屏幕空间虚线材质。
     *
     * @param points 辅助线世界坐标点列。
     * @returns 相切预览辅助线材质。
     */
    public static createTangentGuidePreviewMaterial(points: THREE.Vector3[]): ScreenSpaceDashedLineNodeMaterial {
        return this.createScreenSpaceDashedLineMaterial({
            points,
            color: TANGENT_GUIDE_PREVIEW_COLOR,
            linewidth: POINT_SNAP_GUIDE_LINE_WIDTH,
            dashSize: POINT_SNAP_GUIDE_DASH_SIZE_PX,
            gapSize: POINT_SNAP_GUIDE_GAP_SIZE_PX,
            opacity: POINT_SNAP_GUIDE_OPACITY,
            transparent: true,
            name: LineMaterialName.PointSnapTangentPreviewGuide,
        });
    }

    /**
     * 创建相切高亮辅助线屏幕空间虚线材质。
     *
     * @param points 辅助线世界坐标点列。
     * @returns 相切高亮辅助线材质。
     */
    public static createTangentGuideHighlightMaterial(points: THREE.Vector3[]): ScreenSpaceDashedLineNodeMaterial {
        return this.createScreenSpaceDashedLineMaterial({
            points,
            color: POINT_SNAP_GUIDE_HIGHLIGHT_COLOR,
            linewidth: POINT_SNAP_GUIDE_LINE_WIDTH,
            dashSize: POINT_SNAP_GUIDE_DASH_SIZE_PX,
            gapSize: POINT_SNAP_GUIDE_GAP_SIZE_PX,
            opacity: POINT_SNAP_GUIDE_OPACITY,
            transparent: true,
            name: LineMaterialName.PointSnapTangentHighlightGuide,
        });
    }

    
    /**
     * 创建与点水平/竖直捕捉追踪线屏幕空间虚线材质。
     *
     * 设计说明：
     * 1. 与点 H/V 追踪线是交互引导线，不是草图实体；
     * 2. 虚线节奏应以屏幕像素表达，避免缩放视图后被世界单位拉伸；
     * 3. `ScreenSpaceDashedLineNodeMaterial` 内部持有折线点列 uniform，因此每条追踪线必须独享材质实例。
     *
     * @param points 追踪线世界坐标点列。
     * @returns 与点水平/竖直追踪线屏幕空间虚线材质实例。
     */
    public static createPointHvScreenSpacePreviewMaterial(
        points: THREE.Vector3[],
    ): ScreenSpaceDashedLineNodeMaterial {
        return this.createScreenSpaceDashedLineMaterial({
            points,
            color: POINT_HV_TRACKING_LINE_COLOR,
            linewidth: 1,
            dashSize: POINT_HV_SCREEN_SPACE_DASH_SIZE_PX,
            gapSize: POINT_HV_SCREEN_SPACE_GAP_SIZE_PX,
            opacity: 0.8,
            transparent: true,
            name: LineMaterialName.PointSnapPointHvGuide,
        });
    }
    
    

    /**
     * 获取预览实线材质（单例）。
     * @returns 预览实线材质实例。
     */
    public static getPreviewBasicMaterial(): THREE.LineBasicMaterial {
        if (!this.previewBasicMaterial) {
            this.previewBasicMaterial = new THREE.LineBasicMaterial({
                color: PREVIEW_COLOR,
                linewidth: LINE_WIDTH,
                opacity: 0.8,
                transparent: true,
            });
            this.previewBasicMaterial.name = '预览-实线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.previewBasicMaterial);
    }

    /**
     * 获取预览虚线材质（构造线预览，单例）。
     * @returns 预览虚线材质实例。
     */
    public static getPreviewDashedMaterial(): THREE.LineDashedMaterial {
        if (!this.previewDashedMaterial) {
            this.previewDashedMaterial = new THREE.LineDashedMaterial({
                color: PREVIEW_COLOR,
                linewidth: LINE_WIDTH,
                dashSize: DASH_SIZE,
                gapSize: GAP_SIZE,
                opacity: 0.8,
                transparent: true,
            });
            this.previewDashedMaterial.name = '预览-虚线材质';
        }
        return SketchLineMaterialDepthPolicy.apply(this.previewDashedMaterial);
    }

    /**
     * 获取实体引用预览线材质。
     *
     * @param baseStyle 基础线型状态。
     * @returns 实体引用预览线材质。
     */
    public static getReferencePreviewMaterial(
        baseStyle: SketchEdgeBaseStyle,
    ): SketchLineMaterial {
        if (baseStyle === SketchEdgeBaseStyle.Construction) {
            if (!this.referencePreviewConstructionMaterial) {
                this.referencePreviewConstructionMaterial = new THREE.LineDashedMaterial({
                    color: PREVIEW_COLOR,
                    linewidth: 1,
                    dashSize: DASH_SIZE,
                    gapSize: GAP_SIZE,
                });
                this.referencePreviewConstructionMaterial.name = LineMaterialName.ReferencePreviewConstruction;
            }
            return SketchLineMaterialDepthPolicy.apply(this.referencePreviewConstructionMaterial);
        }

        if (!this.referencePreviewBasicMaterial) {
            this.referencePreviewBasicMaterial = new THREE.LineBasicMaterial({
                color: PREVIEW_COLOR,
                linewidth: LINE_WIDTH,
            });
            this.referencePreviewBasicMaterial.name = LineMaterialName.ReferencePreviewBasic;
        }
        return SketchLineMaterialDepthPolicy.apply(this.referencePreviewBasicMaterial);
    }

    /**
     * 获取实体引用来源高亮线材质。
     *
     * @param baseStyle 基础线型状态。
     * @returns 实体引用来源高亮线材质。
     */
    public static getReferenceSourceHighlightMaterial(
        baseStyle: SketchEdgeBaseStyle,
    ): SketchLineMaterial {
        if (baseStyle === SketchEdgeBaseStyle.Construction) {
            if (!this.referenceSourceHighlightConstructionMaterial) {
                this.referenceSourceHighlightConstructionMaterial = new THREE.LineDashedMaterial({
                    color: REFERENCE_SOURCE_HIGHLIGHT_COLOR,
                    linewidth: 1,
                    dashSize: DASH_SIZE,
                    gapSize: GAP_SIZE,
                    opacity: 0.95,
                    transparent: true,
                });
                this.referenceSourceHighlightConstructionMaterial.name = LineMaterialName.ReferenceSourceHighlightConstruction;
            }
            return SketchLineMaterialDepthPolicy.apply(this.referenceSourceHighlightConstructionMaterial);
        }

        if (!this.referenceSourceHighlightBasicMaterial) {
            this.referenceSourceHighlightBasicMaterial = new THREE.LineBasicMaterial({
                color: REFERENCE_SOURCE_HIGHLIGHT_COLOR,
                linewidth: LINE_WIDTH,
            });
            this.referenceSourceHighlightBasicMaterial.name = LineMaterialName.ReferenceSourceHighlightBasic;
        }
        return SketchLineMaterialDepthPolicy.apply(this.referenceSourceHighlightBasicMaterial);
    }

    /**
     * 获取悬空转换实体引用的共享虚线材质。
     *
     * 悬空状态始终保留虚线节奏；选中和移入仅改变颜色，使实体既能正常交互，
     * 又不会丢失“引用已断开”的视觉含义。
     *
     * @param state 当前交互状态。
     * @returns 对应状态的共享虚线材质。
     */
    public static getReferenceDanglingMaterial(
        state: LineMaterialState,
    ): THREE.LineDashedMaterial {
        const cacheProperty = state === LineMaterialState.Selected
            ? 'referenceDanglingSelectedMaterial'
            : state === LineMaterialState.Hover
                ? 'referenceDanglingHoverMaterial'
                : 'referenceDanglingDefaultMaterial';
        const cachedMaterial = this[cacheProperty];
        if (cachedMaterial) {
            return cachedMaterial;
        }

        const material = new THREE.LineDashedMaterial({
            color: state === LineMaterialState.Selected
                ? LINE_COLOR_SELECTED
                : state === LineMaterialState.Hover
                    ? LINE_COLOR_HOVER
                    : REFERENCE_DANGLING_COLOR,
            linewidth: LINE_WIDTH,
            dashSize: DASH_SIZE,
            gapSize: GAP_SIZE,
        });
        material.name = `草图线-悬空引用-${state}材质`;
        this[cacheProperty] = material;
        return SketchLineMaterialDepthPolicy.apply(material);
    }

    /**
     * 获取转换实体引用计算失败的共享错误材质。
     *
     * @param state 当前交互状态。
     * @returns 对应状态的共享实线材质。
     */
    public static getReferenceFailedMaterial(
        state: LineMaterialState,
    ): THREE.LineBasicMaterial {
        const cacheProperty = state === LineMaterialState.Selected
            ? 'referenceFailedSelectedMaterial'
            : state === LineMaterialState.Hover
                ? 'referenceFailedHoverMaterial'
                : 'referenceFailedDefaultMaterial';
        const cachedMaterial = this[cacheProperty];
        if (cachedMaterial) {
            return cachedMaterial;
        }

        const material = new THREE.LineBasicMaterial({
            color: state === LineMaterialState.Selected
                ? 0xff3b30
                : state === LineMaterialState.Hover
                    ? 0xe5533d
                    : REFERENCE_FAILED_COLOR,
            linewidth: LINE_WIDTH,
        });
        material.name = `草图线-失败引用-${state}材质`;
        this[cacheProperty] = material;
        return SketchLineMaterialDepthPolicy.apply(material);
    }

    /**
     * 创建拖拽留影线材质。
     * @param opacity 留影透明度，默认 0.35（对标 SolidWorks 拖动时原实体半透明观感）。
     * @returns 留影线材质。
     */
    public static createDragSnapshotLineMaterial(
        opacity = 0.35,
    ): SketchLineMaterial {
        // 与业务要求保持一致：留影统一基于默认实线材质克隆
        const material = this.getBasicMaterialByState(LineMaterialState.Default).clone();

        material.transparent = true;
        material.opacity = opacity;
        // 克隆材质在运行时切换透明标志后，必须显式标记更新，确保渲染管线重新编译混合配置。
        material.needsUpdate = true;
        // material.depthWrite = false;
        material.name = '拖拽留影-线-实线材质';
        return SketchLineMaterialDepthPolicy.apply(material);
    }

    /**
     * 创建构造线材质。
     * @returns 构造线虚线材质。
     */
    public static createConstructionLineMaterial(): ThinChainLIneNodeMaterial {
        return this.getConstructionMaterialByState(LineMaterialState.Default);
    }

    /**
     * 创建中心线材质。
     * 说明：当前中心线不再拥有独立视觉体系，统一按构造线处理。
     * @returns 构造线虚线材质。
     */
    public static createCenterLineMaterial(): ThinChainLIneNodeMaterial {
        return this.getConstructionMaterialByState(LineMaterialState.Default);
    }

    
    /**
     * 根据基础线型与交互状态返回最终线材质。
     * @param baseStyle 基础线型状态。
     * @param state 交互状态。
     * @returns 最终线材质。
     * @throws 当基础线型不是普通线或构造线时抛出错误。
     */
    public static getLineMaterialByBaseStyle(
        baseStyle: SketchEdgeBaseStyle,
        state: LineMaterialState,
        isFixed = false,
    ): SketchLineMaterial {
        if (baseStyle === SketchEdgeBaseStyle.Basic && isFixed && state === LineMaterialState.Default) {
            return this.getFixedBasicMaterial();
        }
        if (baseStyle === SketchEdgeBaseStyle.Basic) {
            return this.getBasicMaterialByState(state);
        }
        if (baseStyle === SketchEdgeBaseStyle.Construction) {
            return this.getConstructionMaterialByState(state);
        }

        throw new Error(`未支持的线基础样式：${baseStyle}`);
    }

    /**
     * 按基础线型返回非活动默认态线材质。
     *
     * 说明：
     * 1. 非活动默认态只用于草图编辑视图中的非当前草图对象；
     * 2. hover / selected / creating 仍然走原有语义，不在这里分叉；
     * 3. 这里维持与默认态同一基础线型判断，避免状态机层额外关心线型细节。
     *
     * @param baseStyle 基础线型状态。
     * @param isFixed 是否为固定线。
     * @returns 非活动默认态线材质。
     * @throws 当基础线型不是普通线或构造线时抛出错误。
     */
    public static getInactiveLineMaterialByBaseStyle(
        baseStyle: SketchEdgeBaseStyle,
        isFixed = false,
    ): SketchLineMaterial {
        if (baseStyle === SketchEdgeBaseStyle.Basic && isFixed) {
            return this.getFixedInactiveBasicMaterial();
        }
        if (baseStyle === SketchEdgeBaseStyle.Basic) {
            return this.getBasicInactiveMaterial();
        }
        if (baseStyle === SketchEdgeBaseStyle.Construction) {
            return this.getConstructionInactiveMaterial();
        }

        throw new Error(`未支持的线基础样式：${baseStyle}`);
    }

    /**
     * 获取非活动默认态线材质。
     *
     * 说明：
     * 1. 该方法是对外兼容入口，保留旧的简短命名；
     * 2. 内部仍按基础线型收口到同一条缓存链路；
     * 3. 默认态以更浅的灰色呈现，减少非活动草图的视觉权重。
     *
     * @param baseStyle 基础线型状态。
     * @param isFixed 是否为固定线。
     * @returns 非活动默认态线材质。
     */
    public static getInactiveMaterial(
        baseStyle: SketchEdgeBaseStyle,
        isFixed = false,
    ): SketchLineMaterial {
        return this.getInactiveLineMaterialByBaseStyle(baseStyle, isFixed);
    }

    /**
     * 根据源材质推断基础线型状态。
     * @param sourceMaterial 源材质。
     * @returns 基础线型状态。
     */
    public static resolveBaseStyleBySourceMaterial(
        sourceMaterial?: unknown,
    ): SketchEdgeBaseStyle {
        const normalizedSourceMaterial = this.resolveLineSourceMaterial(sourceMaterial);
        if (isDashDotLIneNodeMaterial(normalizedSourceMaterial)) {
            return SketchEdgeBaseStyle.Construction;
        }

        if (normalizedSourceMaterial instanceof THREE.LineDashedMaterial) {
            return SketchEdgeBaseStyle.Construction;
        }

        return SketchEdgeBaseStyle.Basic;
    }
    

    /**
     * 解析源线材质。
     * @param sourceMaterial 源材质。
     * @returns 可识别的线材质；不存在时返回 null。
     */
    public static resolveLineSourceMaterial(
        sourceMaterial?: unknown,
    ): SketchLineMaterial | null {
        if (!sourceMaterial) {
            return null;
        }

        const firstMaterial = Array.isArray(sourceMaterial)
            ? sourceMaterial[0]
            : sourceMaterial;

        if (isDashDotLIneNodeMaterial(firstMaterial)) {
            return firstMaterial;
        }

        if (firstMaterial instanceof THREE.LineBasicMaterial || firstMaterial instanceof THREE.LineDashedMaterial) {
            return firstMaterial;
        }
        return null;
    }

    /**
     * 按状态返回固定实线材质。
     * @param state 材质状态。
     * @returns 实线材质。
     */
    public static getBasicMaterialByState(state: LineMaterialState): THREE.LineBasicMaterial {
        if (state === LineMaterialState.Selected) {
            if (!this.basicSelectedMaterial) {
                this.basicSelectedMaterial = new THREE.LineBasicMaterial({
                    color: LINE_COLOR_SELECTED,
                    linewidth: LINE_WIDTH,
                });
                this.basicSelectedMaterial.name = LineMaterialName.BasicSelected;
            }
            return SketchLineMaterialDepthPolicy.apply(this.basicSelectedMaterial);
        }

        if (state === LineMaterialState.Hover) {
            if (!this.basicHoverMaterial) {
                this.basicHoverMaterial = new THREE.LineBasicMaterial({
                    color: LINE_COLOR_HOVER,
                    linewidth: LINE_WIDTH,
                });
                this.basicHoverMaterial.name = LineMaterialName.BasicHover;
            }
            return SketchLineMaterialDepthPolicy.apply(this.basicHoverMaterial);
        }

        if (!this.basicDefaultMaterial) {
            this.basicDefaultMaterial = new THREE.LineBasicMaterial({
                color: LINE_COLOR_DEFAULT,
                linewidth: LINE_WIDTH,
            });
            this.basicDefaultMaterial.name = LineMaterialName.BasicDefault;
        }
        return SketchLineMaterialDepthPolicy.apply(this.basicDefaultMaterial);
    }

    /**
     * 获取非活动状态实线默认材质。
     *
     * @returns 非活动状态实线材质。
     */
    public static getBasicInactiveMaterial(): THREE.LineBasicMaterial {
        if (!this.basicInactiveMaterial) {
            this.basicInactiveMaterial = new THREE.LineBasicMaterial({
                color: LINE_COLOR_INACTIVE,
                linewidth: LINE_WIDTH,
                transparent: true,
                opacity: LINE_INACTIVE_OPACITY,
            });
            this.basicInactiveMaterial.name = LineMaterialName.BasicInactive;
        }
        return SketchLineMaterialDepthPolicy.apply(this.basicInactiveMaterial);
    }

    /**
     * 按状态返回固定虚线材质。
     * @param state 材质状态。
     * @returns 虚线材质。
     */
    public static getDashedMaterialByState(state: LineMaterialState): THREE.LineDashedMaterial {
        if (state === LineMaterialState.Selected) {
            if (!this.dashedSelectedMaterial) {
                this.dashedSelectedMaterial = new THREE.LineDashedMaterial({
                    color: LINE_COLOR_SELECTED,
                    linewidth: LINE_WIDTH,
                    dashSize: DASH_SIZE,
                    gapSize: GAP_SIZE,
                });
                this.dashedSelectedMaterial.name = LineMaterialName.DashedSelected;
            }
            return SketchLineMaterialDepthPolicy.apply(this.dashedSelectedMaterial);
        }

        if (state === LineMaterialState.Hover) {
            if (!this.dashedHoverMaterial) {
                this.dashedHoverMaterial = new THREE.LineDashedMaterial({
                    color: LINE_COLOR_HOVER,
                    linewidth: LINE_WIDTH,
                    dashSize: DASH_SIZE,
                    gapSize: GAP_SIZE,
                });
                this.dashedHoverMaterial.name = LineMaterialName.DashedHover;
            }
            return SketchLineMaterialDepthPolicy.apply(this.dashedHoverMaterial);
        }

        if (!this.dashedDefaultMaterial) {
            this.dashedDefaultMaterial = new THREE.LineDashedMaterial({
                color: LINE_COLOR_DEFAULT,
                linewidth: LINE_WIDTH,
                dashSize: DASH_SIZE,
                gapSize: GAP_SIZE,
            });
            this.dashedDefaultMaterial.name = LineMaterialName.DashedDefault;
        }
        return SketchLineMaterialDepthPolicy.apply(this.dashedDefaultMaterial);
    }

    /**
     * 获取非活动状态虚线默认材质。
     *
     * @returns 非活动状态虚线材质。
     */
    public static getDashedInactiveMaterial(): THREE.LineDashedMaterial {
        if (!this.dashedInactiveMaterial) {
            this.dashedInactiveMaterial = new THREE.LineDashedMaterial({
                color: LINE_COLOR_INACTIVE,
                linewidth: LINE_WIDTH,
                dashSize: DASH_SIZE,
                gapSize: GAP_SIZE,
                transparent: true,
                opacity: LINE_INACTIVE_OPACITY,
            });
            this.dashedInactiveMaterial.name = LineMaterialName.DashedInactive;
        }
        return SketchLineMaterialDepthPolicy.apply(this.dashedInactiveMaterial);
    }

    
    /**
     * 按状态返回构造线材质。
     * @param state 材质状态。
     * @returns 构造线材质。
     */
    public static getConstructionMaterialByState(state: LineMaterialState): ThinChainLIneNodeMaterial {
        if (state === LineMaterialState.Selected) {
            return this.createConstructionNodeMaterial(
                LINE_COLOR_SELECTED,
                0.9,
                LineMaterialName.ConstructionSelected,
            );
        }

        if (state === LineMaterialState.Hover) {
            return this.createConstructionNodeMaterial(
                LINE_COLOR_HOVER,
                0.9,
                LineMaterialName.ConstructionHover,
            );
        }

        return this.createConstructionNodeMaterial(
            LINE_COLOR_DEFAULT,
            0.9,
            LineMaterialName.ConstructionDefault,
        );
    }

    /**
     * 获取非活动状态构造线默认材质。
     *
     * @returns 非活动状态构造线材质。
     */
    public static getConstructionInactiveMaterial(): ThinChainLIneNodeMaterial {
        return this.createConstructionNodeMaterial(
            LINE_COLOR_INACTIVE,
            LINE_INACTIVE_OPACITY,
            LineMaterialName.ConstructionInactive,
        );
    }

    /**
     * 创建正式构造线 Node 材质。
     * 说明：
     * 1. 构造线材质依赖每条线自己的 `points` 数据，因此不能复用共享单例；
     * 2. 这里始终返回新实例，避免多条构造线互相覆盖节奏输入；
     * 3. 预览、引用与捕捉辅助虚线仍保持 classic dashed material，不走该路径。
     * @param color 材质颜色。
     * @param opacity 材质透明度。
     * @param name 材质名称。
     * @returns 新的正式构造线 Node 材质。
     */
    private static createConstructionNodeMaterial(
        color: THREE.ColorRepresentation,
        opacity: number,
        name: LineMaterialName,
    ): ThinChainLIneNodeMaterial {
        const material = new ThinChainLIneNodeMaterial({
            color,
            linewidth: 1,
            dashSize: 22,
            gapSize: 4,
            dotSize: 4,
            opacity,
            transparent: true,
        });

        Reflect.set(material, 'name', name);
        return SketchLineMaterialDepthPolicy.apply(material);
    }
    

    /**
     * 获取固定草图线默认材质。
     * @returns 固定线材质。
     */
    public static getFixedBasicMaterial(): THREE.LineBasicMaterial {
        if (!this.fixedDefaultMaterial) {
            this.fixedDefaultMaterial = new THREE.LineBasicMaterial({
                color: LINE_COLOR_FIXED,
                linewidth: LINE_WIDTH,
            });
            this.fixedDefaultMaterial.name = LineMaterialName.FixedDefault;
        }
        return SketchLineMaterialDepthPolicy.apply(this.fixedDefaultMaterial);
    }

    /**
     * 获取非活动状态固定实线默认材质。
     *
     * @returns 非活动状态固定实线材质。
     */
    public static getFixedInactiveBasicMaterial(): THREE.LineBasicMaterial {
        if (!this.fixedInactiveMaterial) {
            this.fixedInactiveMaterial = new THREE.LineBasicMaterial({
                color: LINE_COLOR_INACTIVE,
                linewidth: LINE_WIDTH,
                transparent: true,
                opacity: LINE_INACTIVE_OPACITY,
            });
            this.fixedInactiveMaterial.name = LineMaterialName.FixedInactive;
        }
        return SketchLineMaterialDepthPolicy.apply(this.fixedInactiveMaterial);
    }
    

    /**
     * 获取特征线材质。
     *
     * 设计说明：
     * 1. 默认态透明不可见，与导入件默认「纯着色、无边线」浏览外观对齐；
     * 2. 代理仍保留在场景中以支撑 screen-space 边拾取，Hover/Selected 时再显式着色。
     *
     * @param state 材质状态。
     * @returns 特征线材质。
     */
    public static getFeatureLineMaterialByState(state: LineMaterialState): THREE.LineBasicMaterial {
        if (state === LineMaterialState.Selected) {
            if (!this.featureSelectedMaterial) {
                this.featureSelectedMaterial = new THREE.LineBasicMaterial({
                    color: FEATURE_LINE_COLOR_SELECTED,
                    linewidth: LINE_WIDTH,
                    transparent: false,
                    opacity: 1,
                });
                this.featureSelectedMaterial.name = LineMaterialName.FeatureSelected;
            }
            return this.featureSelectedMaterial;
        }

        if (state === LineMaterialState.Hover) {
            if (!this.featureHoverMaterial) {
                this.featureHoverMaterial = new THREE.LineBasicMaterial({
                    color: FEATURE_LINE_COLOR_HOVER,
                    linewidth: LINE_WIDTH,
                    transparent: false,
                    opacity: 1,
                });
                this.featureHoverMaterial.name = LineMaterialName.FeatureHover;
            }
            return this.featureHoverMaterial;
        }

        if (!this.featureDefaultMaterial) {
            this.featureDefaultMaterial = new THREE.LineBasicMaterial({
                color: FEATURE_LINE_COLOR_DEFAULT,
                linewidth: LINE_WIDTH,
                transparent: this.featureLineDefaultOpacity < 1,
                opacity: this.featureLineDefaultOpacity,
            });
            this.featureDefaultMaterial.name = LineMaterialName.FeatureDefault;
        }
        return this.featureDefaultMaterial;
    }

    
    /**
     * 设置特征线默认态透明度。
     *
     * 设计说明：
     * 1. “显示模型”属于 Viewer 显示规则，不能让 UI 直接改 FeatureLine 材质；
     * 2. 交互状态恢复默认材质时会从材质工厂克隆，因此工厂必须保存当前默认透明度；
     * 3. 已创建的共享默认材质也同步更新，避免新旧默认材质表现不一致。
     *
     * @param opacity 默认态透明度，取值范围为 0 到 1。
     * @throws 当透明度不是 0 到 1 之间的有限数值时抛出错误。
     */
    public static setFeatureLineDefaultOpacity(opacity: number): void {
        if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
            throw new Error(`特征线默认态透明度必须在 0 到 1 之间，当前值：${opacity}`);
        }

        this.featureLineDefaultOpacity = opacity;
        this.syncFeatureDefaultMaterialOpacity();
    }

    /**
     * 读取特征线默认态透明度。
     *
     * @returns 当前特征线默认态透明度。
     */
    public static getFeatureLineDefaultOpacity(): number {
        return this.featureLineDefaultOpacity;
    }

    /**
     * 同步已缓存的特征线默认材质透明度。
     */
    private static syncFeatureDefaultMaterialOpacity(): void {
        if (!this.featureDefaultMaterial) {
            return;
        }

        // 共享默认材质会被后续 clone，必须保持与当前显示模式一致。
        this.featureDefaultMaterial.opacity = this.featureLineDefaultOpacity;
        this.featureDefaultMaterial.transparent = this.featureLineDefaultOpacity < 1;
        this.featureDefaultMaterial.needsUpdate = true;
    }
    

    /**
     * 获取特征面移入时的边界线材质。
     *
     * 设计说明：
     * 1. 默认浏览态特征边线隐藏，仅在鼠标移入某个拓扑面时显示该面的黑色边界；
     * 2. 该材质独立于 FeatureLine 的 Hover/Selected 语义，避免棱边移入时误用黑色边界；
     * 3. 返回共享缓存材质，由交互状态机在刷新周期内配合快照恢复。
     *
     * @returns 面移入边界线材质。
     */
    public static getFeatureFaceHoverBoundaryLineMaterial(): THREE.LineBasicMaterial {
        if (!this.featureFaceHoverBoundaryMaterial) {
            this.featureFaceHoverBoundaryMaterial = new THREE.LineBasicMaterial({
                color: FEATURE_LINE_COLOR_DEFAULT,
                linewidth: LINE_WIDTH,
                transparent: false,
                opacity: 1,
            });
            this.featureFaceHoverBoundaryMaterial.name = LineMaterialName.FeatureFaceHoverBoundary;
        }
        return this.featureFaceHoverBoundaryMaterial;
    }
}
